import { formatDateDisplay } from "@/lib/timezone";
import { AttendanceCellBadge, statusColorClass } from "@/components/attendance/attendance-cell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CATEGORY_LABELS } from "@/lib/types";
import type { CellStatus, MonthlyRow } from "@/lib/types";
import { getMonthlyDaySessions, sessionAbbrev, sessionTitle } from "@/lib/monthly";
import { formatPresencePct, summarizeMonthlyRow } from "@/lib/attendance-pct";
import { cn } from "@/lib/utils";

interface PersonMonthlyDrilldownProps {
  open: boolean;
  onClose: () => void;
  row: MonthlyRow | null;
  tableLabel: string;
  dates: string[];
}

function sessionBadgeText(status: CellStatus, lateMinutes?: number | null): string {
  if (status === "L" && lateMinutes != null) return `L${lateMinutes}`;
  if (status === "pending") return "—";
  return status;
}

export function PersonMonthlyDrilldown({
  open,
  onClose,
  row,
  tableLabel,
  dates,
}: PersonMonthlyDrilldownProps) {
  if (!open || !row) return null;

  const s = summarizeMonthlyRow(row, dates);
  const isShiftRow = Boolean(row.shift_label);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg border bg-white shadow-lg"
        role="dialog"
        aria-modal="true"
      >
        <div className="border-b px-4 py-3">
          <h3 className="text-lg font-semibold">{row.person_name}</h3>
          <p className="text-sm text-muted-foreground">
            {tableLabel}
            {row.category && (
              <>
                {" "}
                · <Badge variant="secondary">{CATEGORY_LABELS[row.category]}</Badge>
              </>
            )}
            {row.shift_label && (
              <>
                {" "}
                · <Badge variant="outline">{row.shift_label}</Badge>
              </>
            )}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            % = P ÷ (P+A+L). MS/FS still count as late or absent
          </p>
        </div>

        <div className="grid grid-cols-4 gap-2 border-b px-4 py-3 text-center text-sm sm:grid-cols-5">
          <Stat label="Present" value={s.present} />
          <Stat label="Late" value={s.late} />
          <Stat label="Absent" value={s.absent} />
          <Stat label="Official (O)" value={s.on_leave} />
          <Stat label="Sick (S)" value={s.sick} />
          <Stat label="Permission (TP)" value={s.taken_permission} />
          <Stat label="Mantras (MS)" value={s.mantra_submitted ?? 0} />
          <Stat label="Fine (FS)" value={s.fine_submitted ?? 0} />
          <Stat label="Holiday" value={s.holiday} />
          <Stat
            label="Present %"
            value={formatPresencePct(s.present_pct, s.all_out)}
            highlight
          />
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <ul className="space-y-1 text-sm">
            {dates.map((d) => {
              const cell = row.cells[d];
              const sessions = getMonthlyDaySessions(cell);

              if (sessions.length === 0) {
                return (
                  <li
                    key={d}
                    className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted/40"
                  >
                    <span className="text-muted-foreground">
                      {formatDateDisplay(d)}
                    </span>
                    <span className="text-slate-300">—</span>
                  </li>
                );
              }

              if (sessions.length === 1 && (!sessions[0].label || isShiftRow)) {
                const session = sessions[0];
                return (
                  <li
                    key={d}
                    className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted/40"
                  >
                    <span className="text-muted-foreground">
                      {formatDateDisplay(d)}
                    </span>
                    <span className="flex items-center gap-2">
                      {(session.holiday_name ?? cell?.holiday_name) && (
                        <span className="text-xs text-violet-700">
                          {session.holiday_name ?? cell?.holiday_name}
                        </span>
                      )}
                      <AttendanceCellBadge
                        cell={{
                          status: session.status,
                          late_minutes: session.late_minutes,
                          holiday_name: session.holiday_name ?? cell?.holiday_name,
                        }}
                      />
                    </span>
                  </li>
                );
              }

              return (
                <li key={d} className="rounded-md px-2 py-1.5 hover:bg-muted/40">
                  <p className="mb-1 text-muted-foreground">
                    {formatDateDisplay(d)}
                  </p>
                  <ul className="space-y-1 border-l-2 border-saffron-100 pl-3">
                    {sessions.map((session, i) => (
                      <li
                        key={`${session.label}-${session.session ?? i}`}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="text-xs text-muted-foreground">
                          {sessionAbbrev(session.session, i)}
                          <span className="ml-1 text-[10px] text-slate-400">
                            {sessionTitle(session.session, session.label, i)}
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          {session.holiday_name && (
                            <span className="text-xs text-violet-700">
                              {session.holiday_name}
                            </span>
                          )}
                          <span
                            className={cn(
                              "inline-flex min-w-[2rem] items-center justify-center rounded border px-1.5 py-0.5 text-xs font-semibold tabular-nums",
                              statusColorClass(session.status)
                            )}
                          >
                            {sessionBadgeText(session.status, session.late_minutes)}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="border-t px-4 py-3">
          <Button variant="outline" className="w-full" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`font-semibold tabular-nums ${highlight ? "text-emerald-700" : ""}`}
      >
        {value}
      </p>
    </div>
  );
}
